// Static seed list of common outpatient primary-care ICD-10 problems used by
// the ICD problem blot's autocomplete. v1 is local-only — no network call,
// instant filter as the clinician types. The list covers the long tail that
// shows up in template-fill output across our pilot specialties (family,
// internal, sleep, cardio, urgent care).
//
// To upgrade later:
//   - Swap searchIcd10() for an API call to a real ICD-10 search endpoint
//     (e.g. clinicaltables.nlm.nih.gov ICD-10-CM, or CMS's official JSON).
//   - Cache results per query in localStorage to keep typing snappy offline.
//   - Add specialty-aware ranking (boost cardio terms when the visit type
//     is cardio, etc.).
//
// Entry shape: { code: 'I10', name: 'Hypertension, essential', aliases: [...] }
// aliases are alternate phrasings the AI / clinician might use ("HTN",
// "high blood pressure") — searchIcd10 matches on both `name` and `aliases`.

const ICD10_COMMON_PROBLEMS = [
  // Cardiovascular
  { code: 'I10', name: 'Hypertension, essential', aliases: ['htn', 'high blood pressure', 'hypertension'] },
  { code: 'I25.10', name: 'Coronary artery disease, native artery', aliases: ['cad', 'coronary artery disease'] },
  { code: 'I48.91', name: 'Atrial fibrillation, unspecified', aliases: ['afib', 'atrial fibrillation'] },
  { code: 'I50.9', name: 'Congestive heart failure, unspecified', aliases: ['chf', 'heart failure'] },
  { code: 'I63.9', name: 'Cerebral infarction, unspecified', aliases: ['stroke', 'cva'] },
  { code: 'G45.9', name: 'Transient cerebral ischemic attack, unspecified', aliases: ['tia'] },
  { code: 'I73.9', name: 'Peripheral vascular disease, unspecified', aliases: ['pad', 'pvd', 'peripheral artery disease'] },
  { code: 'I82.40', name: 'Acute embolism and thrombosis of unspecified deep veins of lower extremity', aliases: ['dvt', 'deep vein thrombosis'] },

  // Endocrine / Metabolic
  { code: 'E11.9', name: 'Type 2 diabetes mellitus without complications', aliases: ['dm2', 'type 2 diabetes', 'diabetes'] },
  { code: 'E11.65', name: 'Type 2 diabetes mellitus with hyperglycemia', aliases: ['uncontrolled diabetes', 'dm2 uncontrolled'] },
  { code: 'R73.03', name: 'Prediabetes', aliases: ['pre-diabetes', 'prediabetes'] },
  { code: 'E78.5', name: 'Hyperlipidemia, unspecified', aliases: ['high cholesterol', 'dyslipidemia'] },
  { code: 'E78.2', name: 'Mixed hyperlipidemia', aliases: [] },
  { code: 'E66.9', name: 'Obesity, unspecified', aliases: ['obesity'] },
  { code: 'E88.81', name: 'Metabolic syndrome', aliases: [] },
  { code: 'E03.9', name: 'Hypothyroidism, unspecified', aliases: ['hypothyroidism', 'low thyroid'] },
  { code: 'E05.90', name: 'Thyrotoxicosis, unspecified', aliases: ['hyperthyroidism', 'hyperthyroid'] },
  { code: 'E55.9', name: 'Vitamin D deficiency, unspecified', aliases: ['low vitamin d', 'vit d deficiency'] },

  // Pulmonary
  { code: 'J44.9', name: 'Chronic obstructive pulmonary disease, unspecified', aliases: ['copd'] },
  { code: 'J45.909', name: 'Unspecified asthma, uncomplicated', aliases: ['asthma'] },
  { code: 'G47.30', name: 'Sleep apnea, unspecified', aliases: ['osa', 'obstructive sleep apnea', 'sleep apnea'] },
  { code: 'G47.00', name: 'Insomnia, unspecified', aliases: ['insomnia'] },
  { code: 'J30.9', name: 'Allergic rhinitis, unspecified', aliases: ['allergies', 'seasonal allergies'] },
  { code: 'J06.9', name: 'Acute upper respiratory infection, unspecified', aliases: ['uri', 'common cold', 'cold'] },
  { code: 'J01.90', name: 'Acute sinusitis, unspecified', aliases: ['sinusitis', 'sinus infection'] },
  { code: 'J32.9', name: 'Chronic sinusitis, unspecified', aliases: [] },
  { code: 'J02.9', name: 'Acute pharyngitis, unspecified', aliases: ['sore throat', 'pharyngitis'] },
  { code: 'J20.9', name: 'Acute bronchitis, unspecified', aliases: ['bronchitis'] },
  { code: 'J18.9', name: 'Pneumonia, unspecified organism', aliases: ['pneumonia'] },
  { code: 'J11.1', name: 'Influenza due to unidentified influenza virus with other respiratory manifestations', aliases: ['influenza', 'flu'] },
  { code: 'U07.1', name: 'COVID-19', aliases: ['covid', 'sars-cov-2'] },

  // GI
  { code: 'K21.9', name: 'Gastro-esophageal reflux disease without esophagitis', aliases: ['gerd', 'reflux', 'acid reflux'] },
  { code: 'K30', name: 'Functional dyspepsia', aliases: ['dyspepsia', 'indigestion'] },
  { code: 'R10.9', name: 'Unspecified abdominal pain', aliases: ['abdominal pain', 'belly pain', 'stomach pain'] },
  { code: 'R10.13', name: 'Epigastric pain', aliases: [] },
  { code: 'K59.00', name: 'Constipation, unspecified', aliases: ['constipation'] },
  { code: 'R19.7', name: 'Diarrhea, unspecified', aliases: ['diarrhea'] },
  { code: 'R11.0', name: 'Nausea', aliases: [] },
  { code: 'R11.10', name: 'Vomiting, unspecified', aliases: ['vomiting'] },

  // Renal / GU
  { code: 'N18.9', name: 'Chronic kidney disease, unspecified', aliases: ['ckd'] },
  { code: 'N39.0', name: 'Urinary tract infection, site not specified', aliases: ['uti'] },
  { code: 'R35.0', name: 'Frequency of micturition', aliases: ['urinary frequency'] },
  { code: 'R30.0', name: 'Dysuria', aliases: [] },
  { code: 'N40.0', name: 'Benign prostatic hyperplasia without lower urinary tract symptoms', aliases: ['bph'] },
  { code: 'N52.9', name: 'Male erectile dysfunction, unspecified', aliases: ['ed', 'erectile dysfunction'] },
  { code: 'N95.1', name: 'Menopausal and female climacteric states', aliases: ['menopause'] },
  { code: 'N92.6', name: 'Irregular menstruation, unspecified', aliases: ['irregular periods'] },

  // Musculoskeletal
  { code: 'M19.90', name: 'Unspecified osteoarthritis, unspecified site', aliases: ['oa', 'osteoarthritis', 'arthritis'] },
  { code: 'M54.50', name: 'Low back pain, unspecified', aliases: ['lbp', 'low back pain', 'back pain'] },
  { code: 'M54.9', name: 'Dorsalgia, unspecified', aliases: ['back pain'] },
  { code: 'M10.9', name: 'Gout, unspecified', aliases: ['gout'] },
  { code: 'M81.0', name: 'Age-related osteoporosis without current pathological fracture', aliases: ['osteoporosis'] },

  // Neurology
  { code: 'R51.9', name: 'Headache, unspecified', aliases: ['headache'] },
  { code: 'G43.909', name: 'Migraine, unspecified, not intractable, without status migrainosus', aliases: ['migraine'] },
  { code: 'G44.209', name: 'Tension-type headache, unspecified, not intractable', aliases: ['tension headache'] },
  { code: 'R42', name: 'Dizziness and giddiness', aliases: ['dizziness', 'vertigo'] },
  { code: 'R55', name: 'Syncope and collapse', aliases: ['syncope', 'passing out'] },

  // Psychiatry
  { code: 'F32.9', name: 'Major depressive disorder, single episode, unspecified', aliases: ['mdd', 'depression'] },
  { code: 'F33.9', name: 'Major depressive disorder, recurrent, unspecified', aliases: ['recurrent depression'] },
  { code: 'F41.1', name: 'Generalized anxiety disorder', aliases: ['gad', 'anxiety'] },
  { code: 'F41.9', name: 'Anxiety disorder, unspecified', aliases: [] },
  { code: 'F43.23', name: 'Adjustment disorder with mixed anxiety and depressed mood', aliases: ['adjustment disorder'] },

  // Hematology
  { code: 'D64.9', name: 'Anemia, unspecified', aliases: ['anemia'] },
  { code: 'D50.9', name: 'Iron deficiency anemia, unspecified', aliases: ['iron deficiency anemia'] },
  { code: 'D51.9', name: 'Vitamin B12 deficiency anemia, unspecified', aliases: ['b12 deficiency'] },

  // Dermatology
  { code: 'L20.9', name: 'Atopic dermatitis, unspecified', aliases: ['atopic dermatitis'] },
  { code: 'L30.9', name: 'Dermatitis, unspecified', aliases: ['eczema', 'dermatitis'] },
  { code: 'L40.9', name: 'Psoriasis, unspecified', aliases: ['psoriasis'] },
  { code: 'R21', name: 'Rash and other nonspecific skin eruption', aliases: ['rash'] },

  // Generalized symptoms (the "if uncertain, use symptom-level" buckets)
  { code: 'R07.9', name: 'Chest pain, unspecified', aliases: ['chest pain'] },
  { code: 'R06.02', name: 'Shortness of breath', aliases: ['sob', 'dyspnea'] },
  { code: 'R05.9', name: 'Cough, unspecified', aliases: ['cough'] },
  { code: 'R53.83', name: 'Other fatigue', aliases: ['fatigue', 'tiredness'] },
  { code: 'R53.1', name: 'Weakness', aliases: ['weakness', 'generalized weakness'] },
  { code: 'R50.9', name: 'Fever, unspecified', aliases: ['fever'] },
  { code: 'R60.9', name: 'Edema, unspecified', aliases: ['edema', 'swelling'] },
];

function normalize(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
}

// Score how well a problem matches the query. Higher is better. 0 = no match.
// Word-prefix match in the name beats substring; alias prefix beats substring
// in alias; full-string match beats prefix.
function scoreMatch(problem, q) {
  if (!q) return 0;
  const nq = normalize(q);
  if (!nq) return 0;
  const nName = normalize(problem.name);
  if (nName === nq) return 1000;
  if (nName.startsWith(nq)) return 800;
  // Word-boundary match in name
  if (new RegExp('(^|\\s)' + nq.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')).test(nName)) return 600;
  if (nName.includes(nq)) return 400;
  // Aliases
  for (const a of problem.aliases || []) {
    const na = normalize(a);
    if (na === nq) return 900;
    if (na.startsWith(nq)) return 700;
    if (na.includes(nq)) return 500;
  }
  // Code match (rare but useful for power users)
  const nCode = normalize(problem.code);
  if (nCode === nq) return 950;
  if (nCode.startsWith(nq)) return 650;
  return 0;
}

// Returns up to `limit` ICD-10 problems matching the query, ordered by score.
// When the query is empty, returns the first `limit` entries in their default
// (clinically-curated) order so the dropdown is useful even before typing.
export function searchIcd10(query, limit = 10) {
  if (!query || !query.trim()) {
    return ICD10_COMMON_PROBLEMS.slice(0, limit);
  }
  const scored = [];
  for (const p of ICD10_COMMON_PROBLEMS) {
    const s = scoreMatch(p, query);
    if (s > 0) scored.push({ p, s });
  }
  scored.sort((a, b) => b.s - a.s);
  return scored.slice(0, limit).map((x) => x.p);
}

// Lookup by exact code (e.g. when re-hydrating a stored ICD blot to display
// the canonical name from the registry rather than whatever the AI emitted).
export function getProblemByCode(code) {
  if (!code) return null;
  const target = String(code).trim();
  return ICD10_COMMON_PROBLEMS.find((p) => p.code === target) || null;
}

// Try to match a free-text problem description against the registry. Used at
// blot-creation time to back-fill the code when the AI emitted just the
// problem text (no code). Returns null if no confident match.
export function inferCodeFromName(name) {
  if (!name) return null;
  const matches = searchIcd10(name, 1);
  if (!matches.length) return null;
  // Only return a code when the top match scored "high confidence" (full or
  // prefix match against name or alias). Substring-only matches are too noisy
  // to auto-tag without clinician confirmation.
  const top = matches[0];
  if (scoreMatch(top, name) >= 600) return top.code;
  return null;
}

export default ICD10_COMMON_PROBLEMS;
